import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Briefcase, Check, CheckCircle2, Dot, Eye, FileText, Folder, FolderCheck, Inbox, Pencil, Search, Trash2, X } from 'lucide-react';
import EnhancedSearch from './EnhancedSearch';
import MoveFolderPicker from './MoveFolderPicker';

const API_BASE = 'https://api.casedb.co';

const mailApiFetch = async (path, options = {}) => {
    const response = await fetch(`${API_BASE}${path}`, {
        ...options,
        credentials: 'include',
        headers: {
            ...(options.body ? { 'Content-Type': 'application/json' } : {}),
            ...(options.headers || {}),
        },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || payload?.message || 'Mailroom request failed');
    }
    return payload;
};

const formatConfidence = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 'Not matched';
    return `${Math.round(numeric)}% match`;
};

const cleanFileNamePart = (value) => String(value || '')
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const formatRecommendationDate = (value) => {
    const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return match ? `${match[1].slice(-2)}.${match[2]}.${match[3]}` : '';
};

export const getMailDisplayFileName = (item, selectedCase = null) => {
    const recommendedName = String(item?.recommended_file_name || '').trim();
    const fallbackName = recommendedName || String(item?.file_name || '').trim() || 'Untitled document';
    const recommendation = item?.filename_recommendation;
    const selectedCaseName = cleanFileNamePart(selectedCase?.case_name || selectedCase?.label || '');
    const manualOverride = recommendation?.manual_override === true
        || String(recommendation?.manual_override || '').toLowerCase() === 'true';

    if (!recommendation || recommendation.is_check_document || manualOverride || !selectedCaseName) {
        return fallbackName;
    }

    const date = formatRecommendationDate(recommendation.document_date);
    const documentName = cleanFileNamePart(recommendation.document_name);
    const extension = cleanFileNamePart(recommendation.extension).replace(/^\.+/, '');
    if (!date || !documentName) {
        return fallbackName;
    }

    return `${date} ${documentName} ${selectedCaseName}${extension ? `.${extension}` : ''}`;
};

const formatDestinationFolder = (path) => {
    const normalized = String(path || '').trim().replace(/^\{\{Name\}\}\/?/, '');
    return normalized || 'Case root';
};

const formatCaseTags = (rawTags) => {
    if (!rawTags) return [];

    let tags = rawTags;
    if (typeof rawTags === 'string') {
        try {
            tags = JSON.parse(rawTags);
        } catch (error) {
            tags = rawTags.split(',');
        }
    }

    if (!Array.isArray(tags)) return [];
    return tags
        .map((tag) => String(tag || '').trim())
        .filter(Boolean)
        .map((tag) => (tag.startsWith('#') ? tag : `#${tag}`));
};

const MailSelectedCaseDetails = ({ selectedCase }) => {
    const tags = formatCaseTags(selectedCase?.tags);
    const assignedUser = selectedCase?.assigned_user || selectedCase?.assignedUser || '';
    const assignedUsername = selectedCase?.assigned_user_username || selectedCase?.assignedUserUsername || '';
    const contactName = selectedCase?.contact_name || selectedCase?.contactName || '';
    const phase = selectedCase?.phase || '';

    return (
        <div className="result-item active mail-selected-case-result">
            <div className="result-content">
                <div className="result-main">
                    <Briefcase size={16} className="result-icon" aria-hidden="true" />
                    <span className="result-title">{selectedCase?.case_name || selectedCase?.label || `Case ${selectedCase?.id}`}</span>
                    <span className="result-type">case <Dot size={16} /> {selectedCase?.id}</span>
                </div>
                {(phase || contactName || assignedUser || tags.length > 0) && (
                    <div className="result-meta">
                        {phase && <span className={`result-status ${phase === 'Archived' ? 'archived-badge' : ''}`}>{phase}</span>}
                        {contactName && <span className="result-status">{contactName}</span>}
                        {assignedUser && (
                            <span className="result-status">
                                {assignedUser}{assignedUsername ? ` (@${assignedUsername})` : ''}
                            </span>
                        )}
                        {tags.length > 0 && (
                            <div className="result-tags-container">
                                {tags.map((tag, index) => <span className="result-status" key={`${tag}-${index}`}>{tag}</span>)}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const MailConfidenceStatus = ({ confidence, needsReview, reasons }) => {
    const reviewReasons = Array.isArray(reasons) ? reasons.filter(Boolean) : [];
    const hasReasons = reviewReasons.length > 0;

    return (
        <div className={`mail-confidence-dropdown ${hasReasons ? 'has-reasons' : ''}`}>
            <span
                className={`mail-status ${needsReview ? 'review' : 'matched'}`}
                tabIndex={hasReasons ? 0 : undefined}
                aria-label={hasReasons ? `${formatConfidence(confidence)}. ${reviewReasons.length} review reasons.` : undefined}
            >
                {formatConfidence(confidence)}
            </span>
            {hasReasons && (
                <div className="mail-confidence-reasons" role="tooltip">
                    <strong>Review reasons</strong>
                    {reviewReasons.map((reason, index) => (
                        <span className="mail-confidence-reason" key={`${reason}-${index}`}>
                            <AlertTriangle size={13} />
                            {reason}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

const InlineMailFileName = ({ item, displayName, editable, editing, value, busy, onStart, onChange, onSave, onCancel }) => {
    if (!editing) {
        if (!editable) {
            return <span className="mail-attachment-name-static">{displayName}</span>;
        }

        return (
            <button type="button" className="mail-attachment-name" onClick={() => onStart(item)} title="Rename file">
                <span>{displayName}</span>
                <Pencil size={13} aria-hidden="true" />
            </button>
        );
    }

    return (
        <form
            className="mail-attachment-edit"
            onSubmit={(event) => {
                event.preventDefault();
                onSave(item);
            }}
        >
            <input
                autoFocus
                aria-label="File name"
                value={value}
                disabled={busy}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                        event.preventDefault();
                        onCancel();
                    }
                }}
            />
            <button type="submit" className="mail-attachment-edit-action save-name" disabled={busy} aria-label="Save file name">
                <Check size={14} />
            </button>
            <button type="button" className="mail-attachment-edit-action cancel-name" disabled={busy} onClick={onCancel} aria-label="Cancel file name edit">
                <X size={14} />
            </button>
        </form>
    );
};

const formatMailDate = (value) => {
    if (!value) return 'Date unavailable';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const checkFieldRows = (item) => {
    const fields = item?.check_fields || item?.verification?.fields || {};
    return [
        ['Claim number', fields.claim_number],
        ['Client name', fields.client_name],
        ['Check amount', fields.check_amount],
        ['Insurance company', fields.insurance_company_name],
        ['Check number', fields.check_number],
        ['Check date', fields.check_date],
    ].filter(([, value]) => value !== undefined && value !== null && value !== '');
};

export const MailIntake = () => {
    const [prefix, setPrefix] = useState('awaiting');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [activeKey, setActiveKey] = useState('');
    const [selectedCase, setSelectedCase] = useState(null);
    const [destinationFolder, setDestinationFolder] = useState('');
    const [showDestinationFolderPicker, setShowDestinationFolderPicker] = useState(false);
    const [editingFileKey, setEditingFileKey] = useState('');
    const [editingFileName, setEditingFileName] = useState('');
    const [actionBusy, setActionBusy] = useState(false);

    const activeItem = useMemo(
        () => items.find((item) => item.key === activeKey) || null,
        [activeKey, items]
    );

    const loadItems = useCallback(async (nextPrefix = prefix) => {
        setLoading(true);
        setFeedback('');
        try {
            const payload = await mailApiFetch(`/mail/files.php?prefix=${encodeURIComponent(nextPrefix)}&time=${Date.now()}`);
            setItems(payload.items || []);
        } catch (error) {
            setItems([]);
            setFeedback(error.message || 'Could not load inbound mail.');
        } finally {
            setLoading(false);
        }
    }, [prefix]);

    useEffect(() => {
        loadItems(prefix);
        setActiveKey('');
        setSelectedCase(null);
        setShowDestinationFolderPicker(false);
    }, [prefix, loadItems]);

    const beginReview = (item) => {
        const isCheck = Boolean(item?.is_check_document || item?.filename_recommendation?.is_check_document);
        const guessedCase = item?.case_guess_id ? {
            id: Number(item.case_guess_id),
            case_name: item.case_guess_name || `Case ${item.case_guess_id}`,
        } : null;

        setActiveKey(item.key);
        setSelectedCase(guessedCase);
        setDestinationFolder(isCheck ? '{{Name}}/Settlements' : '{{Name}}');
        setShowDestinationFolderPicker(false);
        setFeedback('');
    };

    useEffect(() => {
        const guessedCaseId = Number(activeItem?.case_guess_id || 0);
        if (!Number.isFinite(guessedCaseId) || guessedCaseId <= 0) return undefined;

        let cancelled = false;
        const hydrateGuessedCase = async () => {
            try {
                const payload = await mailApiFetch(`/mail/case-search.php?q=${encodeURIComponent(String(guessedCaseId))}&limit=5`);
                const exactMatch = (payload.results || []).find((result) => Number(result.id) === guessedCaseId);
                if (cancelled || !exactMatch) return;
                setSelectedCase((current) => (
                    Number(current?.id) === guessedCaseId ? { ...current, ...exactMatch } : current
                ));
            } catch (error) {
                // The basic AI match remains visible if its optional detail lookup fails.
            }
        };

        hydrateGuessedCase();
        return () => {
            cancelled = true;
        };
    }, [activeItem?.case_guess_id, activeItem?.key]);

    const fetchDestinationFolderContents = useCallback(async (currentFolder = '{{Name}}', page = 1, pageSize = 100) => {
        const caseId = Number(selectedCase?.id || 0);
        if (!Number.isFinite(caseId) || caseId <= 0) {
            return { folders: [], files: [], pagination: {} };
        }

        const params = new URLSearchParams({
            folder_fetch: 'true',
            case_id: String(caseId),
            current_folder: currentFolder || '{{Name}}',
            page: String(page),
            page_size: String(pageSize),
            t: String(Date.now()),
        });
        const payload = await mailApiFetch(`/documents.php?${params.toString()}`);
        return {
            items: payload.items || [],
            folders: payload.folders || [],
            files: payload.files || [],
            metaData: payload.metaData || [],
            pagination: payload.pagination || {},
            currentFolder: payload.current_folder || currentFolder,
            folderPrefix: payload.folder_prefix || '',
        };
    }, [selectedCase?.id]);

    const selectEnhancedSearchCase = (result) => {
        const caseId = Number(result?.value ?? result?.id ?? 0);
        if (!Number.isFinite(caseId) || caseId <= 0) {
            setFeedback('The selected case did not include a valid case ID.');
            return;
        }

        setSelectedCase({
            ...result,
            id: caseId,
            case_name: result?.case_name || result?.label || `Case ${caseId}`,
            contact_name: result?.contact_name || result?.contactName || '',
            assigned_user: result?.assigned_user || result?.assignedUser || '',
            assigned_user_username: result?.assigned_user_username || result?.assignedUserUsername || '',
        });
        setDestinationFolder('{{Name}}');
        setFeedback('');
    };

    const beginFileNameEdit = (item, displayName = getMailDisplayFileName(item)) => {
        setEditingFileKey(item.key);
        setEditingFileName(displayName);
        setFeedback('');
    };

    const cancelFileNameEdit = () => {
        setEditingFileKey('');
        setEditingFileName('');
    };

    const saveFileName = async (item) => {
        if (actionBusy || editingFileKey !== item.key) return;

        const nextName = editingFileName.trim();
        if (!nextName) {
            setFeedback('A file name is required.');
            return;
        }
        if (nextName === getMailDisplayFileName(item, item.key === activeKey ? selectedCase : null)) {
            cancelFileNameEdit();
            return;
        }

        setActionBusy(true);
        setFeedback('');
        try {
            const payload = await mailApiFetch('/mail/files.php', {
                method: 'POST',
                body: JSON.stringify({
                    action: 'rename_file',
                    keys: [item.key],
                    file_name: nextName,
                }),
            });
            const processed = payload.processed?.[0];
            const destinationKey = processed?.destination_key || item.key;
            const savedName = processed?.file_name || nextName;
            if (activeKey === item.key) {
                setActiveKey(destinationKey);
            }
            cancelFileNameEdit();
            await loadItems(prefix);
            setFeedback(`Renamed mail file to ${savedName}.`);
        } catch (error) {
            setFeedback(error.message || 'Could not rename the mail file.');
        } finally {
            setActionBusy(false);
        }
    };

    const fileActiveItem = async () => {
        if (!activeItem || !selectedCase) {
            setFeedback('Choose a case before filing this document.');
            return;
        }

        setActionBusy(true);
        setFeedback('');
        try {
            const preferredFileName = getMailDisplayFileName(activeItem, selectedCase);
            const payload = await mailApiFetch('/mail/files.php', {
                method: 'POST',
                body: JSON.stringify({
                    action: 'file_to_case',
                    keys: [activeItem.key],
                    case_id: selectedCase.id,
                    destination_folder: destinationFolder.trim() || '{{Name}}',
                    use_recommended_file_names: Boolean(preferredFileName),
                    recommended_name_map: preferredFileName ? { [activeItem.key]: preferredFileName } : {},
                }),
            });
            const processed = payload.processed?.[0];
            setFeedback(`Filed ${processed?.file_name || activeItem.file_name} to ${selectedCase.case_name || `case ${selectedCase.id}`}.`);
            setActiveKey('');
            await loadItems(prefix);
        } catch (error) {
            setFeedback(error.message || 'Filing failed.');
        } finally {
            setActionBusy(false);
        }
    };

    const removeItem = async (item, permanent = false) => {
        const prompt = permanent
            ? `Permanently delete ${item.file_name}? This cannot be undone.`
            : `Move ${item.file_name} to removed?`;
        if (!window.confirm(prompt)) return;

        setActionBusy(true);
        setFeedback('');
        try {
            await mailApiFetch('/mail/files.php', {
                method: 'POST',
                body: JSON.stringify({
                    action: permanent ? 'delete_permanently' : 'mark_removed',
                    keys: [item.key],
                }),
            });
            if (activeKey === item.key) setActiveKey('');
            setFeedback(permanent ? 'Mail document permanently deleted.' : 'Mail document moved to removed.');
            await loadItems(prefix);
        } catch (error) {
            setFeedback(error.message || 'Mail action failed.');
        } finally {
            setActionBusy(false);
        }
    };

    return (
        <>
        <div className="mail-intake-layout">
            <aside className="mail-intake-sidebar">
                <div className="mail-intake-view-toggle">
                    <button type="button" className={prefix === 'awaiting' ? 'active' : ''} onClick={() => setPrefix('awaiting')}>
                        <Inbox size={16} /> Review
                    </button>
                    <button type="button" className={prefix === 'filed' ? 'active' : ''} onClick={() => setPrefix('filed')}>
                        <FolderCheck size={16} /> Processed
                    </button>
                    <button type="button" className={prefix === 'removed' ? 'active' : ''} onClick={() => setPrefix('removed')}>
                        <Trash2 size={16} /> Removed
                    </button>
                </div>
                <button type="button" className="action tertiary" onClick={() => loadItems(prefix)} disabled={loading}>
                    {loading ? 'Refreshing…' : 'Refresh mail'}
                </button>
            </aside>

            <div className="mail-intake-content">
                <div className="mail-intake-heading">
                    <div>
                        <h3>{prefix === 'removed' ? 'Removed mail' : prefix === 'filed' ? 'Processed mail' : 'Mail awaiting review'}</h3>
                        <span className="subtext">Inbound mail is kept separate from regular faxes, texts, calls, and notifications.</span>
                    </div>
                    <span className="mail-count-pill">{items.length}</span>
                </div>

                {feedback && <div className="mail-intake-feedback">{feedback}</div>}
                {loading && <div className="activity-item"><span className="subtext">Loading mail documents…</span></div>}
                {!loading && items.length === 0 && (
                    <div className="activity-item mail-intake-empty"><CheckCircle2 size={20} /><span>No mail documents in this view.</span></div>
                )}

                {!loading && items.map((item) => {
                    const fields = checkFieldRows(item);
                    const isActive = item.key === activeKey;
                    const displayFileName = getMailDisplayFileName(item, isActive ? selectedCase : null);
                    const needsReview = item.review_status === 'manual_review' || Number(item.case_guess_confidence || 0) < 95;

                    return (
                        <article className={`activity-item mail-intake-card ${isActive ? 'active' : ''}`} key={item.key}>
                            <div className="mail-intake-card-header">
                                <div className="mail-message-heading">
                                    <h4>{item.mail_subject || 'No subject'}</h4>
                                    <div className="mail-message-meta">
                                        <span>From {item.mail_from || 'unknown sender'}</span>
                                        <span aria-hidden="true">·</span>
                                        <span className={item.case_guess_id ? 'mail-case-reference' : 'mail-case-reference unmatched'}>
                                            {item.case_guess_name || 'No case selected'}
                                        </span>
                                        <span aria-hidden="true">·</span>
                                        <time>{formatMailDate(item.last_modified)}</time>
                                    </div>
                                </div>
                                <div className="mail-intake-statuses">
                                    <MailConfidenceStatus
                                        confidence={item.case_guess_confidence}
                                        needsReview={needsReview}
                                        reasons={item.verification?.manual_review_reasons}
                                    />
                                </div>
                            </div>

                            {item.summary_excerpt && <p className="mail-intake-summary small-text">{item.summary_excerpt}</p>}

                            <div className="mail-attachment-row">
                                <div className="mail-attachment-icon" aria-hidden="true">
                                    <FileText size={17} />
                                </div>
                                <div className="mail-attachment-info">
                                    <InlineMailFileName
                                        item={item}
                                        displayName={displayFileName}
                                        editable={prefix !== 'filed'}
                                        editing={editingFileKey === item.key}
                                        value={editingFileKey === item.key ? editingFileName : displayFileName}
                                        busy={actionBusy}
                                        onStart={(selectedItem) => beginFileNameEdit(selectedItem, displayFileName)}
                                        onChange={setEditingFileName}
                                        onSave={saveFileName}
                                        onCancel={cancelFileNameEdit}
                                    />
                                    <span className="mail-attachment-meta">{item.size_label || 'Size unavailable'}</span>
                                </div>
                                <div className="mail-attachment-actions">
                                    {item.signed_url && <a className="activity-action-btn" href={item.signed_url} target="_blank" rel="noreferrer" title="Open file" aria-label={`Open ${displayFileName}`}><Eye size={16} /></a>}
                                    {prefix !== 'filed' && <button type="button" className="activity-action-btn" onClick={() => beginReview(item)} disabled={actionBusy} title="Review and file" aria-label={`Review and file ${displayFileName}`}><Search size={16} /></button>}
                                    {prefix !== 'filed' && <button type="button" className="activity-action-btn delete-btn" disabled={actionBusy} onClick={() => removeItem(item, prefix === 'removed')} title={prefix === 'removed' ? 'Delete permanently' : 'Move to removed'} aria-label={prefix === 'removed' ? `Delete ${displayFileName} permanently` : `Move ${displayFileName} to removed`}><X size={16} /></button>}
                                </div>
                            </div>

                            <div className="mail-intake-card-body">
                                {item.processing_error && <div className="mail-intake-error"><AlertTriangle size={14} /> {item.processing_error}</div>}

                                {fields.length > 0 && (
                                    <details className="mail-check-details" defaultOpen={needsReview}>
                                        <summary>Check details <span>{fields.length}</span></summary>
                                        <div className="mail-check-grid">
                                            {fields.map(([label, value]) => (
                                                <div key={label}><span>{label}</span><strong>{String(value)}</strong></div>
                                            ))}
                                        </div>
                                    </details>
                                )}

                                {isActive && (
                                    <div className="mail-review-panel">
                                        <div className={`mail-case-search-row ${selectedCase ? 'has-selection' : 'searching'}`}>
                                            {selectedCase ? (
                                                <MailSelectedCaseDetails selectedCase={selectedCase} />
                                            ) : (
                                                <EnhancedSearch
                                                    key={activeItem?.key || 'mail-case-search'}
                                                    expanded
                                                    modal
                                                    initiallyActive
                                                    selectionMode
                                                    caseOnly
                                                    onChange={selectEnhancedSearchCase}
                                                />
                                            )}
                                            {selectedCase && (
                                                <button type="button" className="action tertiary" onClick={() => setSelectedCase(null)} disabled={actionBusy}><Search size={16} /> Search</button>
                                            )}
                                        </div>
                                        <div className="mail-destination-picker">
                                            <div className="mail-destination-choice">
                                                <Folder size={16} aria-hidden="true" />
                                                <div>
                                                    <span>Destination folder</span>
                                                    <strong>{formatDestinationFolder(destinationFolder)}</strong>
                                                </div>
                                            </div>
                                            <button type="button" className="action tertiary" onClick={() => setShowDestinationFolderPicker(true)} disabled={!selectedCase}>Browse</button>
                                        </div>
                                        <div className="mail-review-actions">
                                            <button type="button" className="action tertiary" onClick={() => setActiveKey('')}>Cancel</button>
                                            <button type="button" className="action" onClick={fileActiveItem} disabled={actionBusy || !selectedCase}>{actionBusy ? 'Filing…' : 'File document'}</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </article>
                    );
                })}
            </div>
        </div>
        {showDestinationFolderPicker && selectedCase && (
            <MoveFolderPicker
                isOpen={showDestinationFolderPicker}
                onClose={() => setShowDestinationFolderPicker(false)}
                onSelect={(path) => {
                    setDestinationFolder(path || '{{Name}}');
                    setShowDestinationFolderPicker(false);
                }}
                fetchFolderContents={fetchDestinationFolderContents}
                caseName={selectedCase.case_name || `Case ${selectedCase.id}`}
                currentFolderPath={destinationFolder || '{{Name}}'}
                initialPath={destinationFolder || '{{Name}}'}
                initialSelection={destinationFolder || '{{Name}}'}
                selectMode="folder"
                title="Choose destination folder"
                instructions="Navigate the case folders and choose where this mail document should be filed."
                confirmLabel="Use Folder"
            />
        )}
        </>
    );
};
